/**
 * CPU プレイヤー定義（レジストリ + 思考エンジン）。
 *
 * 設計思想:
 *   - 「CPU プレイヤー」は**抽象的な概念**＝特定の名前（Efren/Intermediate 等）をコードから排除。
 *   - 具体的なプレイヤーは {@link CPU_PLAYERS} 配列のデータとして定義し、追加・差し替えは
 *     データ変更のみで完結する（メソッド・ファイル名のリネーム不要）。
 *   - 全ての呼び出し側は `getCpuPlayer(id)` / `isCpuUserId(id)` を経由し、
 *     プレイヤー名やID を**コード内のリテラルに書かない**こと（ルール: `.claude/rules/no-hardcoded-context.md`）。
 *
 * Phase 2: 複数 CPU 同時出現（マッチング時にランダム選出）/ プレイヤー毎の planShot 差替（style 駆動）。
 *
 * アルゴリズム:
 *   1. 候補生成: 各 (target ball, pocket, power, spin_y) 組み合わせで
 *      ゴーストボール幾何から aim を出し、ShotParams を作る。
 *   2. 各候補で `simulateFullShot`（実物理 N 球シミュレーション）を実行。
 *   3. スコアリング:
 *      +1000  目的球が落ちた（最若球優先）
 *      -10000 手球がスクラッチ
 *      -2000  ファウル: 最初に当たった球が最若球ではない
 *      +位置スコア（次球への距離・カット角・経路クリア）
 *   4. 最高スコアの候補を採用。
 *
 *   style により以下のチューニングが切替わる:
 *     - 'tricky': キックショット候補有効・薄いカット可・パワー強・スピン豊富
 *     - 'safe'  : 直撃のみ・「遠距離で薄いカット」はセーフティ強制・パワー控えめ・出し評価強化
 */

import type { Table } from '../engine/table.js';
import {
  cueBallPos,
  ballPositionById,
  onTablePositions,
  lowestOnTableBallId,
  getPocketAims,
  getTableLayout,
  simulateFullShot,
  objectBallsRemaining,
  placeBallAt,
  ballRadius,
  type ShotParams,
  type FullSimResult,
} from '../sim/world.js';
// 球半径は GPL 境界（world.ts の ballRadius）経由で取得する。
// engine/constants.ts からの直接 import は GPL 非依存維持（CLAUDE.md R-2）のため行わない。
const R = ballRadius;
import {
  getCpuConfig,
  getCpuConfigDefault,
  type StyleConfig,
} from './cpu-config.js';
import { readJson, writeJson } from '../state/storage.js';

// ============================================================
// CPU プレイヤー レジストリ
// ============================================================

/**
 * 思考スタイル。配列に追加するだけで新スタイルを増やせる前提で抽象化。
 *  - 'tricky': クッション・バンク・薄いカットを多用するアグレッシブ型
 *  - 'safe'  : 手玉の動き最小・厚いカット優先・無理ならセーフティで完璧に隠す
 */
export type CpuStyle = 'tricky' | 'safe';

/** CPU プレイヤーの定義データ。配列に追加するだけで新規 CPU が登場可能になる。 */
export interface CpuPlayer {
  /** ロビーの仮想ユーザー識別子（'cpu-*' で始める慣習）。 */
  readonly id: string;
  /** UI 表示名（ロビー・バナー等）。 */
  readonly displayName: string;
  /** 思考スタイル（planShot の挙動を切替）。 */
  readonly style: CpuStyle;
  /** 補足説明（プロファイル・ツールチップ用・任意）。 */
  readonly description?: string;
}

// ============================================================
// CPU プレイヤー レジストリ（localStorage 永続化）
//
// 設計:
//   - 組込（{@link BUILTIN_CPU_PLAYERS}）は初回起動時の初期値・「組込に戻す」の復元元。
//   - 実体は localStorage キー `pool.cpu.players.v1` に保存（プレイヤー追加・編集・削除可）。
//   - 全プレイヤーが削除可能。完全に空になっても動作する（ロビーから CPU が消えるのみ）。
// ============================================================

/**
 * 組込 CPU プレイヤー定義（初回起動時の初期値・「組込に戻す」の復元元）。
 * 実際に使われる一覧は {@link getCpuPlayers} 経由で取得すること。
 */
export const BUILTIN_CPU_PLAYERS: readonly CpuPlayer[] = [
  {
    id: 'cpu-efren',
    displayName: 'Efren',
    style: 'tricky',
    description: 'The Magician（バンク・クッションを多用するトリッキーな攻め）',
  },
  {
    id: 'cpu-intermediate',
    displayName: 'Intermediate',
    style: 'safe',
    description: '厚いカット中心の安定派。無理なら完璧なセーフティで手玉を隠す（中級レベル）',
  },
];

const PLAYERS_STORAGE_KEY = 'pool.cpu.players.v1';

const isCpuStyle = (s: unknown): s is CpuStyle => s === 'tricky' || s === 'safe';

const sanitizeCpuPlayer = (raw: unknown): CpuPlayer | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id.length === 0) return null;
  if (typeof r.displayName !== 'string' || r.displayName.length === 0) return null;
  if (!isCpuStyle(r.style)) return null;
  const out: CpuPlayer = {
    id: r.id,
    displayName: r.displayName,
    style: r.style,
    ...(typeof r.description === 'string' ? { description: r.description } : {}),
  };
  return out;
};

const loadCpuPlayers = (): readonly CpuPlayer[] => {
  const raw = readJson<unknown>(PLAYERS_STORAGE_KEY);
  if (raw === null) return BUILTIN_CPU_PLAYERS; // 初回起動: 組込を返す
  if (!Array.isArray(raw)) return BUILTIN_CPU_PLAYERS;
  const sanitized: CpuPlayer[] = [];
  const seenIds = new Set<string>();
  for (const item of raw) {
    const p = sanitizeCpuPlayer(item);
    if (!p) continue;
    if (seenIds.has(p.id)) continue; // 重複 id は最初の1件のみ採用
    seenIds.add(p.id);
    sanitized.push(p);
  }
  return sanitized; // 空配列でもそのまま返す（「全削除」を尊重）
};

let _playersCache: readonly CpuPlayer[] | null = null;

const invalidateCache = (): void => {
  _playersCache = null;
};

/** 現役 CPU プレイヤー一覧（localStorage 永続化済み）を取得する。 */
export const getCpuPlayers = (): readonly CpuPlayer[] => {
  if (!_playersCache) _playersCache = loadCpuPlayers();
  return _playersCache;
};

/** 一覧をまるごと保存する（内部ヘルパー）。 */
const saveCpuPlayers = (players: readonly CpuPlayer[]): void => {
  writeJson(PLAYERS_STORAGE_KEY, players);
  invalidateCache();
};

/** 一意な CPU プレイヤー id を生成する（'cpu-' プレフィックス + 短いランダム文字列）。 */
export const generateCpuId = (): string => {
  const existing = new Set(getCpuPlayers().map((p) => p.id));
  for (let attempt = 0; attempt < 100; attempt++) {
    const suffix = Math.random().toString(36).slice(2, 8);
    const candidate = `cpu-${suffix}`;
    if (!existing.has(candidate)) return candidate;
  }
  // 万一の衝突連発に備えてタイムスタンプフォールバック
  return `cpu-${Date.now().toString(36)}`;
};

/** 新規 CPU プレイヤーを追加する（id は既に振られている前提）。 */
export const addCpuPlayer = (player: CpuPlayer): void => {
  const players = getCpuPlayers();
  if (players.some((p) => p.id === player.id)) return; // 既存 id は無視
  saveCpuPlayers([...players, player]);
};

/** 指定 id の CPU プレイヤー定義を部分更新する（id 変更は不可）。 */
export const updateCpuPlayer = (
  id: string,
  patch: Partial<Omit<CpuPlayer, 'id'>>,
): void => {
  const players = getCpuPlayers();
  const next = players.map((p) => (p.id === id ? { ...p, ...patch } : p));
  saveCpuPlayers(next);
};

/** 指定 id の CPU プレイヤーを削除する。 */
export const deleteCpuPlayer = (id: string): void => {
  const players = getCpuPlayers().filter((p) => p.id !== id);
  saveCpuPlayers(players);
};

/** CPU プレイヤー一覧を組込（Efren/Intermediate）に戻す。 */
export const resetCpuPlayersToBuiltin = (): void => {
  saveCpuPlayers(BUILTIN_CPU_PLAYERS);
};

/** 指定 userId が CPU プレイヤーか判定する。 */
export const isCpuUserId = (userId: string): boolean =>
  getCpuPlayers().some((p) => p.id === userId);

/** id で CPU プレイヤー定義を取得する。存在しなければ null。 */
export const getCpuPlayer = (id: string): CpuPlayer | null =>
  getCpuPlayers().find((p) => p.id === id) ?? null;

/**
 * ロビーに出現させる CPU 一覧。
 * spawn 条件（「人間 0 人」）は呼び出し側（loopback-lobby）で判断する。
 */
export const lobbyCpuPlayers = (): readonly CpuPlayer[] => getCpuPlayers();

// ============================================================
// 思考エンジン（style 駆動）
// ============================================================

/** 仰角は固定（寝かせ撞き）。 */
const DEFAULT_ELEVATION = 0.05;
/**
 * ゴーストボール距離係数。target からの距離 = 2R × FACTOR。
 * 2R = 球直径 = 衝突点までの正しい距離。FACTOR は衝突確実化のための僅かな余裕。
 *
 * ⚠ 旧値 2.001 は誤り（target から 4R の地点に ghost を置き、AI 照準がずっとズレていた）。
 *   コメント「2R を僅かに超える」と整合させて FACTOR ≒ 1.0 + ε に修正（2026-06-09）。
 */
const GHOST_BALL_FACTOR = 1.001;
/** ポケットインセット係数（中心より僅か内側を狙う＝ジャム回避）。 */
const POCKET_INSET = 0.94;
/** 次球評価で「理想出し距離」[m]（≒シャフト 1 本）。 */
const POSITION_IDEAL_DIST = 0.45;
/** 次球評価で「理想カット角」[rad]（30°）。 */
const POSITION_IDEAL_CUT = (30 * Math.PI) / 180;

/**
 * style 毎のチューニングパラメータ ({@link StyleConfig}) は cpu-config.ts に集約。
 * 値は {@link getStyleConfig} で取得する（組込デフォルト + localStorage オーバーレイ）。
 * 管理者画面の CPU 調整 UI で実行時に書き換え可能。
 */

// ============================================================
// 厳密ティア制スコアリング（修正版）
//
// 設計意図:
//   旧スコアリング（pot=+1000・safety+bonus が同一スケール）では
//   「出し位置不良のポットがセーフティに負ける」「セーフティが weak hit でノーレール反則を起こす」
//   といった問題が頻発した。本実装ではティアを大きく離して優先順位を厳格化する。
//
//   - Tier A (POT): ポット成功（最若球を撞いて落とした）。base=100,000・最強優先。
//   - Tier B (SAFETY): 合法セーフティ（最若球先撞き + レール接触 or 落球）。base=50,000。
//   - Tier C (LEGAL_HIT_NO_RAIL): 最若球を撞いただけ（レール接触なし=no-rail foul リスク）。base=10,000・回避推奨。
//   - Tier D (WRONG_HIT): 最若球以外を先撞き＝確定ファウル。base=-100,000。
//   - Tier E (SCRATCH): 手球スクラッチ＝最悪。base=-200,000。
//
//   各ティア内の差分（出しスコア・隠しボーナス・パワー）はティア境界を越えない範囲（±10,000）に
//   クランプされ、上位ティアが下位ティアに負けることを構造的に防ぐ。
//
//   キックショットは「直撃で同じ結果が得られない場合のみ最善」になるように
//   tricky 側でも -KICK_PENALTY のペナルティを与える（直撃ポットがあるならキックポットは選ばれない）。
// ============================================================

const TIER_POT = 100_000;
const TIER_SAFETY = 50_000;
const TIER_LEGAL_HIT_NO_RAIL = 10_000;
const TIER_WRONG_HIT = -100_000;
const TIER_SCRATCH = -200_000;
/** ティア内差分の上限/下限（境界越えを防ぐためのクランプ）。 */
const TIER_INNER_RANGE = 9_000;
/** キックショットへのペナルティ（直撃が成立する場合に直撃を必ず選ばせる）。
 *  TIER_INNER_RANGE より大きい値にすることで「出しが悪い直撃ポット」が「出しが良いキックポット」に
 *  逆転されるのを防ぐ（boundary: 100k+9k > 100k-20k+9k → direct ≥ kick が常に成立）。 */
const KICK_PENALTY = 20_000;
/**
 * カロムポット（球同士をぶつけて target を入れる軌道）へのペナルティ。
 * - safe スタイルでは TIER_SAFETY (50k) を下回るほどの重ペナルティ → 「ぶつけて入れる」は採用しない
 * - tricky スタイルでは中程度ペナルティ → 直撃不可能時のみ採用
 */
const CAROM_PENALTY_SAFE = 60_000; // 100k - 60k = 40k < TIER_SAFETY (50k)
const CAROM_PENALTY_TRICKY = 30_000; // 100k - 30k = 70k > TIER_SAFETY (50k)

// STYLE_CONFIG（組込デフォルト）の実体は cpu-config.ts の STYLE_DEFAULTS に移動した。
// 実行時の値は getStyleConfig(style) 経由で取得すること（localStorage オーバーレイが効く）。

/** CPU 思考結果。 */
export interface CpuPlan {
  readonly shot: ShotParams;
  readonly note: string;
}

/** 角度を [-π,π] に正規化。 */
const normAngle = (a: number): number => {
  let x = a % (Math.PI * 2);
  if (x > Math.PI) x -= Math.PI * 2;
  if (x < -Math.PI) x += Math.PI * 2;
  return x;
};

/**
 * ゴーストボール位置: 「的玉中心 + (ポケット→的玉) 方向 × 2R×係数」。
 * 手球中心がここに来たとき、的玉がポケット方向へ正しく出ていく。
 */
const ghostBallPos = (
  target: { readonly x: number; readonly y: number },
  pocket: { readonly x: number; readonly y: number },
): { x: number; y: number } => {
  const dx = target.x - pocket.x;
  const dy = target.y - pocket.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return { x: target.x, y: target.y };
  const ux = dx / len;
  const uy = dy / len;
  return {
    x: target.x + ux * 2 * R * GHOST_BALL_FACTOR,
    y: target.y + uy * 2 * R * GHOST_BALL_FACTOR,
  };
};

/** 出しスコア: 手球停止位置から「次球」が「シャフト 1 本以内・カット 30°以下」に近いほど高得点。 */
const positionScore = (
  cueStopX: number,
  cueStopY: number,
  nextTarget: { readonly x: number; readonly y: number } | null,
  pocketsXY: readonly { readonly x: number; readonly y: number }[],
): number => {
  if (!nextTarget) return 0;
  const dist = Math.hypot(nextTarget.x - cueStopX, nextTarget.y - cueStopY);
  const dirToNext = Math.atan2(nextTarget.y - cueStopY, nextTarget.x - cueStopX);
  let bestNextCut = Math.PI / 2;
  for (const p of pocketsXY) {
    const dirToPocket = Math.atan2(p.y - nextTarget.y, p.x - nextTarget.x);
    let cut = Math.abs(normAngle(dirToPocket - dirToNext));
    if (cut > Math.PI / 2) cut = Math.PI - cut;
    if (cut < bestNextCut) bestNextCut = cut;
  }
  const distScore =
    dist <= POSITION_IDEAL_DIST
      ? 100
      : Math.max(-200, 100 - (dist - POSITION_IDEAL_DIST) * 300);
  const cutScore =
    bestNextCut <= POSITION_IDEAL_CUT
      ? 100
      : Math.max(-100, 100 - ((bestNextCut - POSITION_IDEAL_CUT) * 180) / Math.PI * 3);
  return distScore + cutScore;
};

/**
 * ティア制の候補スコア。`scorePotCandidate` も `scoreSafetyCandidate` も同一スケールで返るため、
 * ループ内では単純な `score > bestScore` 比較が成立する。
 *
 * ティア決定ロジック:
 *   - cueScratched → TIER_SCRATCH（最悪）
 *   - firstHitId !== targetId → TIER_WRONG_HIT（確定ファウル）
 *   - pocketedIds に targetId 含む → TIER_POT（ポット成功）
 *   - 上記以外で「pocketedIds または railedNumberedIds または cueRailed」あり → TIER_SAFETY（合法）
 *   - 上記以外（的玉に当てただけ・レール接触なし）→ TIER_LEGAL_HIT_NO_RAIL（no-rail foul リスク）
 *
 * 各ティア内の差分は TIER_INNER_RANGE（±9,000）にクランプ。
 */

/** ポット候補のスコア。出し位置・カット角・距離で差分を付け、禁止軌道は強制降格。
 *
 * @param cueToTargetDist 手玉→的玉の距離 [m]（遠距離判定に使用）
 * @param cutAngle このショットのカット角 [rad]（cutAngleHardLimit / farCutAngleLimit 判定に使用）
 */
const scorePotCandidate = (
  result: FullSimResult,
  targetId: number,
  nextTargetPos: { readonly x: number; readonly y: number } | null,
  pocketsXY: readonly { readonly x: number; readonly y: number }[],
  cfg: StyleConfig,
  isKick: boolean,
  cueToTargetDist: number,
  cutAngle: number,
): number => {
  if (result.cueScratched) return TIER_SCRATCH;
  if (result.firstHitId !== targetId) return TIER_WRONG_HIT;
  const isKickLike = isKick || result.cueRailedBeforeFirstHit;
  const targetPotted = result.pocketedIds.includes(targetId);

  // forbidPreRailContact=true: キック類軌道（明示的キック + 偶発的カスリ）は強制却下。
  if (cfg.forbidPreRailContact && isKickLike) {
    return TIER_LEGAL_HIT_NO_RAIL - 30_000;
  }

  // カット角ハード上限超過のみ強制却下（80°+ の事実上不可能な薄カット）。
  // 30°〜80° の中間域はペナルティで自然と「厚撞き選好」になるよう設計。
  if (cutAngle > cfg.cutAngleHardLimit) {
    return TIER_LEGAL_HIT_NO_RAIL - 30_000;
  }
  // 遠距離 × 薄カット は強い減点を後段で行う（強制却下はせず、他に手がなければ採用させる）。

  if (!targetPotted) {
    const isLegalAfterHit =
      result.anyBallRailedAfterFirstHit || result.pocketedIds.length > 0;
    if (isLegalAfterHit) {
      return TIER_LEGAL_HIT_NO_RAIL + (isKickLike ? -KICK_PENALTY : 0);
    }
    return TIER_LEGAL_HIT_NO_RAIL - 30_000 + (isKickLike ? -KICK_PENALTY : 0);
  }
  // ポット成功。スコア計算は出し・カット角・距離・カロムの加減点をすべて innerScore へ集約。
  let inner = 0;
  if (nextTargetPos) {
    const ps =
      positionScore(result.cueStopX, result.cueStopY, nextTargetPos, pocketsXY) *
      cfg.positionScoreWeight;
    inner += Math.max(-TIER_INNER_RANGE, Math.min(TIER_INNER_RANGE, ps * 10));
  }
  // カット角ペナルティ: 30°超で段階的に減点（30°→0 / 60°→-9000 / 90°→-18000）。
  // 厚撞き候補が無ければ薄カットも採用されるが、TIER_SAFETY (+50,000) に負ける場合が増える。
  const cutDeg = (cutAngle * 180) / Math.PI;
  if (cutDeg > 30) inner -= (cutDeg - 30) * 300;
  // 遠距離 × 薄カット の追加ペナルティ。設定の閾値超過分に応じて減点。
  if (
    Number.isFinite(cfg.farDistanceThreshold) &&
    cueToTargetDist > cfg.farDistanceThreshold &&
    cutAngle > cfg.farCutAngleLimit
  ) {
    const farExcess = (cueToTargetDist - cfg.farDistanceThreshold) * 8000; // 1m 超過で -8000
    const cutExcess = ((cutAngle - cfg.farCutAngleLimit) * 180) / Math.PI * 300; // 1°超過で -300
    inner -= Math.min(20_000, farExcess + cutExcess);
  }
  if (isKickLike) inner -= KICK_PENALTY;
  if (result.caromedIds.includes(targetId)) {
    inner -= cfg.forbidCaromPots ? CAROM_PENALTY_SAFE : CAROM_PENALTY_TRICKY;
  }
  // 加算値が下限を超えても、ポット成功であることは保証する（ティア内クランプは下限のみ広げる）。
  inner = Math.max(-TIER_INNER_RANGE - 70_000, Math.min(TIER_INNER_RANGE, inner));
  return TIER_POT + inner;
};

/**
 * セーフティ候補のスコア。
 *  - 合法判定: firstHit==target かつ (rail接触 or 落球)。これがないと TIER_LEGAL_HIT_NO_RAIL（合法と認めない）。
 *  - 'safe' スタイル時のみ「手玉遠ざけ + スヌーカー成立 + クッション至近」をボーナスとして加算。
 */
const scoreSafetyCandidate = (
  result: FullSimResult,
  targetId: number,
  cfg: StyleConfig,
  table: Table,
): number => {
  if (result.cueScratched) return TIER_SCRATCH;
  if (result.firstHitId !== targetId) return TIER_WRONG_HIT;
  // 9 ボール no-rail foul 厳密判定: 「target hit 後」のレール接触 OR ポケットイン。
  const isLegal =
    result.anyBallRailedAfterFirstHit || result.pocketedIds.length > 0;
  // セーフティループは safe スタイルでは dirToTarget ± 0.15 rad に絞られている（pre-rail 軌道は通常生まれない）。
  // tricky スタイルではワイルド 16 方向だが、forbidPreRailContact=false なので問題なし。
  // → 一律 KICK_PENALTY ベースで処理し、強制降格は行わない（grazing 誤判定を回避）。
  // ポットしてしまったセーフティはセーフティではない = TIER_POT 扱い（出し評価なし）。
  // pre-rail 軌道（=キック/バンク経由のポット）と カロム（球同士をぶつけて入れる）は減点。
  if (result.pocketedIds.includes(targetId)) {
    let penalty = 0;
    if (result.cueRailedBeforeFirstHit) penalty -= KICK_PENALTY;
    if (result.caromedIds.includes(targetId)) {
      penalty -= cfg.forbidCaromPots ? CAROM_PENALTY_SAFE : CAROM_PENALTY_TRICKY;
    }
    return TIER_POT + penalty;
  }
  if (!isLegal) {
    // ノーレール反則 = TIER_LEGAL_HIT_NO_RAIL より大幅に低く（safety candidate より下に降格）。
    return TIER_LEGAL_HIT_NO_RAIL - 30_000;
  }
  // 合法セーフティ。隠し評価ボーナスを加算（±TIER_INNER_RANGE クランプ）。
  let inner = 0;
  if (cfg.enableHidingSafety) {
    const targetPos = ballPositionById(table, targetId);
    if (targetPos) {
      const dx = targetPos.x - result.cueStopX;
      const dy = targetPos.y - result.cueStopY;
      const dist = Math.hypot(dx, dy);
      // 1) 手玉停止位置 → 最若球距離が遠いほど +（1.5m で +5000）。
      //    旧 1.2m=+3000 → 強化（離れているほど相手は厚く狙えない）。
      inner += Math.min(5000, dist * 3500);
      // 2) スヌーカー成立判定: 手玉↔最若球の直線上に他の的玉が挟まる。
      //    旧 +5000 → +8000 に強化（隠れている候補を最優先する）。
      //    判定半径も 2R から 1.6R に絞り「ほぼ完全に隠れる」のみを高評価に。
      const len = Math.hypot(dx, dy);
      if (len > 1e-9) {
        const ux = dx / len;
        const uy = dy / len;
        // 複数球が直線上にあれば「二重スヌーカー」として更にボーナス。
        let blockers = 0;
        for (const p of onTablePositions(table)) {
          if (p.id === 0 || p.id === targetId) continue;
          const px = p.x - result.cueStopX;
          const py = p.y - result.cueStopY;
          const t = px * ux + py * uy;
          if (t <= 0 || t >= len) continue;
          const perp = Math.abs(px * (-uy) + py * ux);
          if (perp < 1.6 * R) blockers++;
        }
        if (blockers >= 1) inner += 8000;
        if (blockers >= 2) inner += 1000; // 二重スヌーカーは更に強化
      }
      // 3) 手玉停止がクッション至近 → ボーナス強化 +500 → +1500（壁際は相手が打ちにくい）。
      const layout = getTableLayout();
      const distToRail = Math.min(
        layout.halfX - Math.abs(result.cueStopX),
        layout.halfY - Math.abs(result.cueStopY),
      );
      if (distToRail < 0.05) inner += 1500;
      else if (distToRail < 0.10) inner += 700;
    }
  }
  inner = Math.max(-TIER_INNER_RANGE, Math.min(TIER_INNER_RANGE, inner));
  return TIER_SAFETY + inner;
};

/** 次球（最若球の次に若い盤上球）の位置を取得。なければ null。 */
const findNextTargetPos = (
  table: Table,
  currentTargetId: number,
): { x: number; y: number } | null => {
  let nextId: number | null = null;
  for (const p of onTablePositions(table)) {
    if (p.id === 0 || p.id === currentTargetId) continue;
    if (nextId === null || p.id < nextId) nextId = p.id;
  }
  if (nextId === null) return null;
  return ballPositionById(table, nextId);
};

/**
 * 思考: 全候補シミュレーション → 最高スコア採用。
 * style により候補生成・閾値・スコア重みが切替わる。
 *
 * @param table 現在の盤面
 * @param style プレイヤースタイル（オーバーレイ未指定時のフォールバック）
 * @param cpuId 対戦中の CPU プレイヤー id（指定時はプレイヤー個別オーバーレイを反映）。
 *   省略時は style のデフォルト値で動作する（テスト・旧呼び出し互換）。
 */
export const planShot = (
  table: Table,
  style: CpuStyle = 'tricky',
  cpuId?: string,
): CpuPlan | null => {
  if (objectBallsRemaining(table) === 0) return null;

  const cfg: StyleConfig = cpuId
    ? getCpuConfig(cpuId, style)
    : getCpuConfigDefault(style);
  const cue = cueBallPos(table);
  const targetId = lowestOnTableBallId(table);
  if (targetId === null) return null;
  const target = ballPositionById(table, targetId);
  if (!target) return null;

  const pocketsRaw = getPocketAims();
  const pockets = pocketsRaw.map((p) => ({
    x: p.x * POCKET_INSET,
    y: p.y * POCKET_INSET,
  }));
  const nextTargetPos = findNextTargetPos(table, targetId);
  const layout = getTableLayout();
  const halfX = layout.halfX;
  const halfY = layout.halfY;

  // ブレイク特例: 9 ボールで残球 9（フルラック）= ブレイクショット。
  // ブレイクは style に依存しない（強パワーで散らす）。
  const isBreakShot = objectBallsRemaining(table) === 9;
  if (isBreakShot) {
    const dirToTarget = Math.atan2(target.y - cue.y, target.x - cue.x);
    let bestBreakScore = -Infinity;
    let bestBreakParams: ShotParams | null = null;
    let bestBreakResult: FullSimResult | null = null;
    for (const aimDelta of [-0.035, -0.017, 0, 0.017, 0.035]) {
      for (const breakPower of [6.0, 7.0, 8.0]) {
        for (const spinY of [-0.15, 0, 0.1]) {
          const params: ShotParams = {
            angle: dirToTarget + aimDelta,
            power: breakPower,
            offsetX: 0,
            offsetY: spinY,
            elevation: DEFAULT_ELEVATION,
          };
          const result = simulateFullShot(table, params);
          let score = -2000;
          if (result.cueScratched) {
            score = -10000;
          } else if (result.firstHitId === 1) {
            score = 100;
            if (result.pocketedIds.length > 0) score += 500;
            if (result.railedNumberedIds.length >= 4) score += 500;
            score += breakPower * 5;
          }
          if (score > bestBreakScore) {
            bestBreakScore = score;
            bestBreakParams = params;
            bestBreakResult = result;
          }
        }
      }
    }
    if (bestBreakParams) {
      return {
        shot: bestBreakParams,
        note: `BREAK shot score=${bestBreakScore.toFixed(0)} pwr=${bestBreakParams.power} pocketed=[${bestBreakResult?.pocketedIds.join(',')}] railed=${bestBreakResult?.railedNumberedIds.length} firstHit=${bestBreakResult?.firstHitId}`,
      };
    }
  }

  interface AimCandidate {
    readonly angle: number;
    readonly kind: 'direct' | 'kick';
    readonly pocketIndex: number;
    readonly rail?: 'top' | 'bottom' | 'left' | 'right';
    /** 直撃時のカット角 [rad]。kick 候補は 0 を入れる（cutAngleHardLimit を適用しない）。 */
    readonly cut: number;
  }
  const aimCandidates: AimCandidate[] = [];
  // 手玉 → 的玉の距離（cfg.farDistanceThreshold 判定に使う）。
  const cueToTargetDist = Math.hypot(target.x - cue.x, target.y - cue.y);

  // 1) 直撃候補（カット角制限は撤廃: 物理シミュレータがスクラッチ/失敗を判定する）
  //    物理的に不可能なカット角 (≥ 90°) のみ除外。実際のスコアリングは scorePotCandidate に委譲。
  for (let pIdx = 0; pIdx < pockets.length; pIdx++) {
    const pocket = pockets[pIdx]!;
    const ghost = ghostBallPos(target, pocket);
    const aim = Math.atan2(ghost.y - cue.y, ghost.x - cue.x);
    const dirCueToTarget = Math.atan2(target.y - cue.y, target.x - cue.x);
    const dirTargetToPocket = Math.atan2(pocket.y - target.y, pocket.x - target.x);
    let cut = Math.abs(normAngle(dirTargetToPocket - dirCueToTarget));
    if (cut > Math.PI / 2) cut = Math.PI - cut;
    // 物理的に当たらない角度のみ除外（カット ≥ 90° は backside hit になり物理的に target が pocket 方向へ進まない）。
    if (cut >= Math.PI / 2 - 0.05) continue;
    aimCandidates.push({ angle: aim, kind: 'direct', pocketIndex: pIdx, cut });
  }

  // 2) 1-レール キック候補（safe スタイルでは無効化）
  if (cfg.enableKicks) {
    const railOffset = R;
    const kickConfigs: ReadonlyArray<{
      readonly rail: 'top' | 'bottom' | 'left' | 'right';
      readonly mirrorX: number;
      readonly mirrorY: number;
    }> = [
      { rail: 'top', mirrorX: target.x, mirrorY: 2 * (halfY - railOffset) - target.y },
      { rail: 'bottom', mirrorX: target.x, mirrorY: -2 * (halfY - railOffset) - target.y },
      { rail: 'left', mirrorX: -2 * (halfX - railOffset) - target.x, mirrorY: target.y },
      { rail: 'right', mirrorX: 2 * (halfX - railOffset) - target.x, mirrorY: target.y },
    ];
    for (const kcfg of kickConfigs) {
      const aim = Math.atan2(kcfg.mirrorY - cue.y, kcfg.mirrorX - cue.x);
      aimCandidates.push({ angle: aim, kind: 'kick', pocketIndex: -1, rail: kcfg.rail, cut: 0 });
      for (const delta of [-0.17, 0.17]) {
        aimCandidates.push({
          angle: aim + delta,
          kind: 'kick',
          pocketIndex: -1,
          rail: kcfg.rail,
          cut: 0,
        });
      }
    }
  }

  let bestScore = -Infinity;
  let bestParams: ShotParams | null = null;
  let bestNote = '';
  let bestResult: FullSimResult | null = null;
  let attempts = 0;

  for (const cand of aimCandidates) {
    for (const power of cfg.powerCandidates) {
      for (const spinY of cfg.spinYCandidates) {
        attempts++;
        const params: ShotParams = {
          angle: cand.angle,
          power,
          offsetX: 0,
          offsetY: spinY,
          elevation: DEFAULT_ELEVATION,
        };
        const result = simulateFullShot(table, params);
        const score = scorePotCandidate(
          result,
          targetId,
          nextTargetPos,
          pockets,
          cfg,
          cand.kind === 'kick',
          cueToTargetDist,
          cand.cut,
        );
        if (score > bestScore) {
          bestScore = score;
          bestParams = params;
          bestResult = result;
          const kindLabel =
            cand.kind === 'kick' ? `kick(${cand.rail})` : `direct#${cand.pocketIndex}`;
          bestNote = `${kindLabel} pwr=${power} spinY=${spinY.toFixed(2)} score=${score.toFixed(0)} pocketed=[${result.pocketedIds.join(',')}] firstHit=${result.firstHitId} rail=${result.railedNumberedIds.length}/cue=${result.cueRailed ? 'y' : 'n'}`;
        }
      }
    }
  }

  // セーフティ候補生成。
  //  - safe スタイル: 「的玉方向の弱パワー直撃」のみ。pre-rail 軌道を生まないように direction を絞る。
  //  - tricky スタイル: 一様な多方向探索（任意角度 + 的玉方向バリエーション）。
  // 共通の評価器 scoreSafetyCandidate が合法判定とスタイル別ペナルティを担う。
  const dirToTarget = Math.atan2(target.y - cue.y, target.x - cue.x);
  const safetyAngles: number[] = [];
  if (cfg.forbidPreRailContact) {
    // safe: 的玉方向 ± バリエーション（pre-rail 不可）。
    // 旧 ±0.15rad の狭い範囲から ±0.35rad (約 ±20°) まで拡張し、薄カット〜厚カットの全域で
    // 「他球の裏に手玉を運ぶ」軌道を試せるようにする。スピン Y も外側ループで掛けるため、
    // ドロー/フォロー組合せで停止位置を細かく刻める（CPU セーフティ精度の改善・2026-06-12）。
    for (const delta of [-0.35, -0.22, -0.15, -0.08, -0.03, 0, 0.03, 0.08, 0.15, 0.22, 0.35]) {
      safetyAngles.push(dirToTarget + delta);
    }
  } else {
    // tricky: 一様 16 方向 + 的玉方向微小バリエーション。
    for (let i = 0; i < 16; i++) safetyAngles.push((i / 16) * Math.PI * 2);
    for (const delta of [-0.12, -0.05, 0, 0.05, 0.12]) {
      safetyAngles.push(dirToTarget + delta);
    }
  }
  // セーフティ候補の縦回転バリエーション。
  //  - 引き (-) で「弾いたあと手玉を後退させて他球の裏に隠す」
  //  - 0 / 弱い押し (+) で「手玉を前方の他球まで滑らせて隠す」
  // 強い前後回転（|spinY| ≥ 0.7）はセーフティでは事故率が上がるので除外する。
  const safetySpins = (cfg.spinYCandidates as readonly number[]).filter(
    (s) => Math.abs(s) <= 0.6,
  );
  // 空配列対策（極端な設定でも候補 0 にならないように 0 を最低限保証）。
  const effSpins = safetySpins.length > 0 ? safetySpins : [0];
  for (const dirAngle of safetyAngles) {
    for (const sPow of cfg.safetyPowers) {
      for (const spinY of effSpins) {
        attempts++;
        const params: ShotParams = {
          angle: dirAngle,
          power: sPow,
          offsetX: 0,
          offsetY: spinY,
          elevation: DEFAULT_ELEVATION,
        };
        const result = simulateFullShot(table, params);
        const score = scoreSafetyCandidate(result, targetId, cfg, table);
        if (score > bestScore) {
          bestScore = score;
          bestParams = params;
          bestResult = result;
          bestNote = `safety dir=${((dirAngle * 180) / Math.PI).toFixed(0)}° pwr=${sPow} spinY=${spinY.toFixed(2)} score=${score.toFixed(0)} firstHit=${result.firstHitId} pocketed=[${result.pocketedIds.join(',')}] rail=${result.railedNumberedIds.length}/cue=${result.cueRailed ? 'y' : 'n'}`;
        }
      }
    }
  }

  if (!bestParams) {
    return {
      shot: {
        angle: 0,
        power: 0.01,
        offsetX: 0,
        offsetY: 0,
        elevation: DEFAULT_ELEVATION,
      },
      note: `internal: no candidates generated (ball#${targetId})`,
    };
  }

  return {
    shot: bestParams,
    note: `[${style}] ball#${targetId} ${bestNote} (${attempts} candidates evaluated)${bestResult && !bestResult.settled ? ' [timeout]' : ''}`,
  };
};

// ============================================================
// Ball-In-Hand 自由配置（CPU 用）
// ============================================================

/** BIH 配置プラン: 配置位置と、その位置から planShot した結果。 */
export interface CpuBihPlan {
  /** 配置先 x [m]。 */
  readonly placeX: number;
  /** 配置先 y [m]。 */
  readonly placeY: number;
  /** その位置からのベストショット。 */
  readonly plan: CpuPlan;
}

/**
 * 障害物を考慮した「手玉↔最若球の直撃可否」をジオメトリで近似判定する。
 * 完全な衝突判定ではないが、明らかにブロックされている候補位置を高速に除外できる。
 */
const isLineOfSightClear = (
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  table: Table,
  ignoreIds: ReadonlyArray<number>,
): boolean => {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return false;
  const ux = dx / len;
  const uy = dy / len;
  for (const p of onTablePositions(table)) {
    if (ignoreIds.includes(p.id)) continue;
    const px = p.x - fromX;
    const py = p.y - fromY;
    const t = px * ux + py * uy;
    if (t < 2 * R || t > len - 2 * R) continue; // 線分の端点付近は除外
    const perp = Math.abs(px * (-uy) + py * ux);
    if (perp < 2 * R) return false; // 球が直線上にある
  }
  return true;
};

/**
 * BIH 用の手玉配置候補を生成する。
 * 戦略: 各ポケット方向に対して「ポケット → 最若球 → 延長線上 D の位置」を候補とする
 * （手玉をその位置に置けば、ポケットへの厚カット直撃ラインが取れる）。
 * 加えて、最若球から各方向に等距離の汎用候補も入れて多様性を確保。
 */
const bihCandidatePositions = (
  target: { readonly x: number; readonly y: number },
  pockets: readonly { readonly x: number; readonly y: number }[],
  table: Table,
  targetId: number,
): { x: number; y: number }[] => {
  const layout = getTableLayout();
  const halfX = layout.halfX - 2 * R; // 盤内クランプ
  const halfY = layout.halfY - 2 * R;
  // 他球と重ならない候補のみ受理する（手玉重置時の自動解決による予期せぬ移動を防止）。
  const otherBalls = onTablePositions(table).filter((p) => p.id !== 0);
  const positionOk = (x: number, y: number): boolean => {
    for (const p of otherBalls) {
      if (Math.hypot(p.x - x, p.y - y) < 2.05 * R) return false;
    }
    return true;
  };
  const positions: { x: number; y: number }[] = [];

  // 各ポケットについて、3 つの距離（近・中・遠）で「手玉を置く理想ライン上の点」を候補化。
  const DISTANCES = [0.30, 0.55, 0.85];
  for (const pocket of pockets) {
    const dx = target.x - pocket.x;
    const dy = target.y - pocket.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;
    const ux = dx / len;
    const uy = dy / len;
    for (const d of DISTANCES) {
      const cx = target.x + ux * d;
      const cy = target.y + uy * d;
      // 盤内クランプ
      const cxC = Math.max(-halfX, Math.min(halfX, cx));
      const cyC = Math.max(-halfY, Math.min(halfY, cy));
      // 直線で最若球に当てられるかをジオメトリ近似でチェック（クランプで位置が変わった場合は除外）。
      if (
        Math.abs(cxC - cx) < 0.01 &&
        Math.abs(cyC - cy) < 0.01 &&
        positionOk(cxC, cyC) &&
        isLineOfSightClear(cxC, cyC, target.x, target.y, table, [0, targetId])
      ) {
        positions.push({ x: cxC, y: cyC });
      }
    }
  }
  // 多様性確保: 最若球の周囲 8 方向 × 距離 0.4m の汎用候補。
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const cx = target.x + Math.cos(a) * 0.4;
    const cy = target.y + Math.sin(a) * 0.4;
    const cxC = Math.max(-halfX, Math.min(halfX, cx));
    const cyC = Math.max(-halfY, Math.min(halfY, cy));
    if (
      Math.abs(cxC - cx) < 0.01 &&
      Math.abs(cyC - cy) < 0.01 &&
      isLineOfSightClear(cxC, cyC, target.x, target.y, table, [0, targetId])
    ) {
      positions.push({ x: cxC, y: cyC });
    }
  }
  return positions;
};

/**
 * BIH 配置を計画する: 候補位置を試して最良の planShot 結果を返す。
 *
 * 各候補について手玉を一時配置 → planShot → スコア（最初の bestNote から復元するのは難しいので
 * 単純に「ポット成功か」を主指標、tie-break で出しスコアを使う）→ 元位置に復元、を繰り返す。
 *
 * @param table 現在の盤面（呼び出し時点の手玉位置はファウル後の位置）
 * @param style CPU スタイル
 * @returns BIH 配置プラン or null（候補ゼロ／思考失敗時）
 */
export const planBallInHand = (
  table: Table,
  style: CpuStyle = 'tricky',
  cpuId?: string,
): CpuBihPlan | null => {
  if (objectBallsRemaining(table) === 0) return null;
  const targetId = lowestOnTableBallId(table);
  if (targetId === null) return null;
  const target = ballPositionById(table, targetId);
  if (!target) return null;
  const pocketsRaw = getPocketAims();
  const pockets = pocketsRaw.map((p) => ({
    x: p.x * POCKET_INSET,
    y: p.y * POCKET_INSET,
  }));

  const candidates = bihCandidatePositions(target, pockets, table, targetId);
  if (candidates.length === 0) {
    // 候補ゼロ: 現在の手玉位置で planShot するしかない（フォールバック）。
    const fallback = planShot(table, style, cpuId);
    return fallback
      ? {
          placeX: cueBallPos(table).x,
          placeY: cueBallPos(table).y,
          plan: fallback,
        }
      : null;
  }

  // 元の手玉位置を保持（候補評価後に復元）。
  const originalCue = cueBallPos(table);
  let bestPlan: CpuPlan | null = null;
  let bestPos: { x: number; y: number } | null = null;
  // ベスト比較のため、note 内の score=NNNN 値をパースする（簡易・整数前提）。
  const parseScore = (note: string): number => {
    const m = note.match(/score=(-?\d+)/);
    return m ? parseInt(m[1]!, 10) : -Infinity;
  };
  let bestScore = -Infinity;

  for (const cand of candidates) {
    placeBallAt(table, 0, cand.x, cand.y);
    const plan = planShot(table, style, cpuId);
    if (!plan) continue;
    const score = parseScore(plan.note);
    if (score > bestScore) {
      bestScore = score;
      bestPlan = plan;
      bestPos = cand;
    }
  }

  // 手玉を元位置に戻す（呼び出し側で確定した時点で再配置する）。
  placeBallAt(table, 0, originalCue.x, originalCue.y);

  if (!bestPlan || !bestPos) {
    return null;
  }
  return {
    placeX: bestPos.x,
    placeY: bestPos.y,
    plan: bestPlan,
  };
};

// ============================================================
// プッシュアウト判断（CPU 用）
// ============================================================

/**
 * 現在の盤面で「明確なポット」が成立するかをジオメトリのみで高速判定する。
 * プッシュアウト宣言の判断に使う（明白なポットがある = プッシュアウトしない）。
 *
 * 閾値は push-out 判定専用（厳しめ）:
 *  - カット角 < 50°（薄カットはプッシュアウト推奨）
 *  - 手玉↔最若球↔ポケットすべて直線で line-of-sight が通る
 */
export const hasClearPotOpportunity = (
  table: Table,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _style: CpuStyle = 'tricky',
): boolean => {
  const PUSH_OUT_CUT_LIMIT = (50 * Math.PI) / 180; // 明白判定は 50° 以下のみ
  const targetId = lowestOnTableBallId(table);
  if (targetId === null) return false;
  const target = ballPositionById(table, targetId);
  if (!target) return false;
  const cue = cueBallPos(table);
  const pocketsRaw = getPocketAims();
  for (const p of pocketsRaw) {
    const pocket = { x: p.x * POCKET_INSET, y: p.y * POCKET_INSET };
    // 手玉→ゴーストボール の line-of-sight チェック
    const ghost = ghostBallPos(target, pocket);
    if (!isLineOfSightClear(cue.x, cue.y, ghost.x, ghost.y, table, [0, targetId])) continue;
    // カット角チェック
    const dirCueToTarget = Math.atan2(target.y - cue.y, target.x - cue.x);
    const dirTargetToPocket = Math.atan2(pocket.y - target.y, pocket.x - target.x);
    let cut = Math.abs(normAngle(dirTargetToPocket - dirCueToTarget));
    if (cut > Math.PI / 2) cut = Math.PI - cut;
    if (cut > PUSH_OUT_CUT_LIMIT) continue;
    // 的玉→ポケットの line-of-sight チェック
    if (!isLineOfSightClear(target.x, target.y, pocket.x, pocket.y, table, [0, targetId])) continue;
    return true; // 1 つでも条件を満たすポケットがあれば clear
  }
  return false;
};

/**
 * プッシュアウト時のショットプラン。
 * 「ラグ風の弱いショット」: 的玉方向に弱パワーで撞き、手玉が的玉付近・難しい位置で止まることを狙う。
 * 相手にも難しい局面を返すための簡易ヒューリスティック（厳密な最適化はしない）。
 */
export const planPushOutShot = (table: Table): CpuPlan | null => {
  if (objectBallsRemaining(table) === 0) return null;
  const targetId = lowestOnTableBallId(table);
  if (targetId === null) return null;
  const target = ballPositionById(table, targetId);
  if (!target) return null;
  const cue = cueBallPos(table);
  // 的玉方向に弱パワー（cue が target 付近で止まる狙い）。
  // プッシュアウト中はルール無視なので、スクラッチさえしなければ何でも良い。
  const angle = Math.atan2(target.y - cue.y, target.x - cue.x);
  return {
    shot: {
      angle,
      power: 0.9, // 弱め: 軽くタップして手玉を的玉付近へ移動
      offsetX: 0,
      offsetY: 0,
      elevation: DEFAULT_ELEVATION,
    },
    note: `push-out: aim toward ball#${targetId}, soft tap`,
  };
};
